import type { SellerProfile, SellerServices, SellerSnapshot } from '../sellerContracts';
import { api } from './client';

const S = '/api/seller';
const paise = (rupees: number) => Math.round(rupees * 100);

function uiStatus(status: string) {
  const s = status.toUpperCase();
  if (s === 'PROCESSING') return 'processing';
  if (s === 'SHIPPED' || s === 'DELIVERED') return 'completed';
  if (s === 'CANCELLED' || s === 'REFUNDED') return 'canceled';
  return 'pending';
}

function fulfill(status: string) {
  const s = status.toUpperCase();
  if (s === 'SHIPPED') return 'shipped';
  if (s === 'DELIVERED') return 'delivered';
  if (s === 'PROCESSING') return 'picking';
  return 'unallocated';
}

type ApiOrder = {
  id: string; number: string; status: string; placedAt: string; customerName?: string;
  pricing?: { total?: number; payable?: number };
  items?: { productId: string; name: string; price: number; quantity: number }[];
  lines?: { productId: string; name: string; price: number; quantity: number }[];
};

export const httpSellerServices: SellerServices = {
  async snapshot(): Promise<SellerSnapshot> {
    const me = await api<{ seller: { id: string; name: string; email: string; slug: string; status: string; commissionBps: number; createdAt?: string; profile?: { phone?: string } } }>(`${S}/me`);
    const [ordersPage, offersPage, earnings, payoutsPage, rmasPage] = await Promise.all([
      api<{ items: ApiOrder[] }>(`${S}/orders?pageSize=50`),
      api<{ items: { id: string; price: number; stock: number; status: string; product?: { name: string; sku: string } }[] }>(`${S}/offers?pageSize=50`),
      api<{ balance: number; items: { id: string; type: string; amount: number; balanceAfter: number; description: string; createdAt: string }[] }>(`${S}/earnings`),
      api<{ items: { id: string; amount: number; status: string; method?: string; createdAt: string; paidAt?: string }[] }>(`${S}/payouts?pageSize=50`),
      api<{ items: { id: string; status: string; items?: unknown }[] }>(`${S}/rmas?pageSize=50`),
    ]);
    const seller: SellerProfile = {
      id: me.seller.id, name: me.seller.name, email: me.seller.email, slug: me.seller.slug,
      status: me.seller.status === 'ACTIVE' ? 'approved' : me.seller.status.toLowerCase(),
      commissionBps: me.seller.commissionBps, phone: me.seller.profile?.phone, createdAt: me.seller.createdAt,
    };
    return {
      seller,
      orders: (ordersPage.items ?? []).map((o) => ({
        id: o.id, number: o.number, customerName: o.customerName ?? 'Guest',
        grandTotal: paise(o.pricing?.payable ?? o.pricing?.total ?? 0),
        status: uiStatus(o.status), fulfillmentStatus: fulfill(o.status), createdAt: o.placedAt,
        items: (o.lines ?? o.items ?? []).map((it) => ({ ...it, price: paise(it.price), sellerId: seller.id })),
      })),
      offers: (offersPage.items ?? []).map((o) => ({
        id: o.id, name: o.product?.name ?? o.id, sku: o.product?.sku ?? '',
        price: paise(o.price), stock: o.stock, status: o.status === 'ACTIVE' ? 'published' : 'draft',
      })),
      ledger: (earnings.items ?? []).map((e) => ({
        id: e.id, type: e.type, amount: paise(e.amount), balance: paise(e.balanceAfter), description: e.description, createdAt: e.createdAt,
      })),
      payouts: (payoutsPage.items ?? []).map((p) => ({
        id: p.id, amount: paise(p.amount), status: p.status.toLowerCase(), method: p.method, processedAt: p.paidAt,
      })),
      rmas: (rmasPage.items ?? []).map((r) => ({
        id: r.id, number: r.id.slice(-6), customerName: 'Customer', reason: String((r as { resolution?: string }).resolution ?? ''), status: String(r.status).toLowerCase(),
      })),
      balance: paise(earnings.balance ?? 0),
    };
  },
  async transition(orderId, status, tracking) {
    const map: Record<string, string> = { processing: 'PROCESSING', shipped: 'SHIPPED', delivered: 'DELIVERED', completed: 'DELIVERED' };
    await api(`${S}/orders/${orderId}/transition`, {
      method: 'POST',
      body: JSON.stringify({ status: map[status] ?? status.toUpperCase(), tracking }),
    });
  },
  async updateProfile(input) {
    const row = await api<{ id: string; name: string; email: string; slug: string; status: string; commissionBps: number }>(`${S}/profile`, {
      method: 'PUT', body: JSON.stringify(input),
    });
    return { ...row, status: row.status === 'ACTIVE' ? 'approved' : row.status.toLowerCase() };
  },
  async requestPayout(amount) {
    await api(`${S}/payouts`, { method: 'POST', body: JSON.stringify({ amount: Math.round(amount / 100) }) });
  },
  async rmaAction(id, status) {
    await api(`${S}/rmas/${id}/action`, { method: 'POST', body: JSON.stringify({ status: status.toUpperCase() }) });
  },
};
